import React, { useContext, useEffect, useState } from 'react';

import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { doc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroller';

import {
  companiesRef,
  getCompanyById
} from '../../api/firebase/firestore/company';
import { getIssueInfiniteScrollfromPublisher } from '../../api/firebase/firestore/issue';
import { mediasRef } from '../../api/firebase/firestore/media';
import { getUserInfo } from '../../api/firebase/firestore/user';
import { blue } from '../../constants/Colors';
import {
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
} from '../../constants/Location';
import { status } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';
import NothingGivenList from '../uiParts/NothingGivenList';

import CustomLoading from './CustomLoading';
import IssueSearchCard from './IssueSearchCard';
import PublisherIssueCard from './PublisherIssueCard';

import type { Issue } from '../../api/firebase/firestore/issue/issueType';
import type { timestamp } from './RegisterIssueCard';
import type { Dayjs } from 'dayjs';
import type { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';

const styles = {
  card: {
    '@media screen and (max-width:470px)': {
      mt: '12px'
    },
    mt: '24px'
  },
  contactAllButton: {
    ':focus': {
      background: blue
    },
    ':hover': {
      background: blue
    },
    '@media screen and (max-width:960px)': {
      height: '30px',
      padding: '4px 10px',
      width: '154px'
    },
    background: blue,
    boxShadow: 'none',
    height: '36px',
    width: '200px'
  },
  contactCheck: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    margin: '22px 0 0 24px'
  },
  list: {
    '@media screen and (max-width:470px)': {
      margin: '0 16px'
    },
    margin: '0 24px'
  },
  mypageHeader,
  mypageHeaderTitle,
  mypageWrapper
};

const PublisherIssueList = (): JSX.Element => {
  const { t } = useTranslation();

  const { currentUser } = useContext(AuthContext);

  const [loading, setLoading] = useState<boolean>(false);

  const [showList, setShowList] = React.useState<boolean>(false);

  const [hasMore, setHasMore] = useState(true);

  const [issueList, setIssueList] = useState<Issue[]>([]);

  const [searchedIssueList, setSearchedIssueList] = useState<Issue[]>([]);

  const [searchIssueTitle, setSearchIssueTitle] = useState<string>('');

  const [searchStartDate, setSearchStartDate] = React.useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({ end: null, start: null });
  const [searchApplicationDate, setSearchApplicationDate] = React.useState<{
    start: Dayjs | null;
    end: Dayjs | null;
  }>({ end: null, start: null });

  const [searchGenre, setSearchGenre] = useState<string | number | null>(null);
  const [searchBudget, setSearchBudget] = useState<string | number | null>(
    null
  );
  const [searchPurpose, setSearchPurpose] = useState<string | number | null>(
    null
  );
  const [searchMediaType, setSearchMediaType] = useState<
    string | number | null
  >(null);

  const [searchMenu, setSearchMenu] = useState<number | null>(null);

  const handleSearchInputChange = (
    issueTitle: string,
    startDate: { start: Dayjs | null; end: Dayjs | null },
    applicationDate: { start: Dayjs | null; end: Dayjs | null },
    genre: string | number | null,
    budget: string | number | null,
    purpose: string | number | null,
    mediaType: string | number | null,
    menu: number | null
  ): void => {
    if (issueTitle) {
      setSearchIssueTitle(issueTitle);
    } else {
      setSearchIssueTitle('');
    }

    if (genre) {
      setSearchGenre(genre);
    } else {
      setSearchGenre(null);
    }

    if (budget) {
      setSearchBudget(budget);
    } else {
      setSearchBudget(null);
    }

    if (purpose) {
      setSearchPurpose(purpose);
    } else {
      setSearchPurpose(null);
    }

    if (mediaType) {
      setSearchMediaType(mediaType);
    } else {
      setSearchMediaType(null);
    }

    if (menu) {
      setSearchMenu(menu);
    } else {
      setSearchMenu(null);
    }

    if (startDate.start && startDate.end) {
      setSearchStartDate({ end: startDate.end, start: startDate.start });
    } else if (startDate.start) {
      setSearchStartDate({ end: null, start: startDate.start });
    } else if (startDate.end) {
      setSearchStartDate({ end: startDate.end, start: null });
    } else {
      setSearchStartDate({ end: null, start: null });
    }

    if (applicationDate.start && applicationDate.end) {
      setSearchApplicationDate({
        end: applicationDate.end,
        start: applicationDate.start
      });
    } else if (applicationDate.start) {
      setSearchApplicationDate({ end: null, start: applicationDate.start });
    } else if (applicationDate.end) {
      setSearchApplicationDate({ end: applicationDate.end, start: null });
    } else {
      setSearchApplicationDate({ end: null, start: null });
    }
  };

  useEffect(() => {
    let filteredIssues = issueList;
    if (searchIssueTitle) {
      filteredIssues = filteredIssues.filter((issue: Issue) =>
        issue?.name?.includes(searchIssueTitle)
      );
    }

    if (searchGenre) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) => issue?.genre === searchGenre
      );
    }

    if (searchBudget) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) => issue?.budget === searchBudget
      );
    }

    if (searchPurpose) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) => issue.purpose && issue?.purpose[0] === searchPurpose
      );
    }

    if (searchMediaType) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) =>
          issue.mediaType && issue?.mediaType[0] === searchMediaType
      );
    }

    if (searchMenu) {
      filteredIssues = filteredIssues.filter(
        (issue: Issue) =>
          Array.isArray(issue?.menu) &&
          (issue?.menu as number[]).includes(searchMenu)
      );
    }

    if (searchStartDate.start && searchStartDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchStartDate.start &&
          searchStartDate.end &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') >=
            searchStartDate.start.format('YYYY/MM/DD') &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
      );
    } else if (searchStartDate.start) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchStartDate.start &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') >= searchStartDate.start.format('YYYY/MM/DD')
      );
    } else if (searchStartDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchStartDate.end &&
          dayjs
            .unix((value.startDate as timestamp)?.seconds)
            .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
      );
    }

    if (searchApplicationDate.start && searchApplicationDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchApplicationDate.start &&
          searchApplicationDate.end &&
          dayjs
            .unix((value.applicationDate as timestamp).start?.seconds)
            .format('YYYY/MM/DD') >=
            searchApplicationDate.start.format('YYYY/MM/DD') &&
          dayjs
            .unix((value.applicationDate as timestamp).end?.seconds)
            .format('YYYY/MM/DD') <=
            searchApplicationDate.end.format('YYYY/MM/DD')
      );
    } else if (searchApplicationDate.start) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchApplicationDate.start &&
          dayjs
            .unix((value.applicationDate as timestamp).start?.seconds)
            .format('YYYY/MM/DD') >=
            searchApplicationDate.start.format('YYYY/MM/DD')
      );
    } else if (searchApplicationDate.end) {
      filteredIssues = filteredIssues.filter(
        (value: DocumentData) =>
          searchApplicationDate.end &&
          dayjs
            .unix((value.applicationDate as timestamp).end?.seconds)
            .format('YYYY/MM/DD') <=
            searchApplicationDate.end.format('YYYY/MM/DD')
      );
    }

    filteredIssues = filteredIssues.filter(
      (issue, index, self) =>
        index === self.findIndex((i) => i.issueId === issue.issueId)
    );

    setSearchedIssueList(filteredIssues);
  }, [
    searchIssueTitle,
    searchStartDate,
    searchApplicationDate,
    searchGenre,
    searchBudget,
    searchPurpose,
    searchMediaType,
    searchMenu
  ]);

  const appendIssues = async (
    snapshot: Array<QueryDocumentSnapshot<DocumentData>>
  ): Promise<void> => {
    // 承認されたcompanyに絞る
    const promises: Array<Promise<Issue | null>> = snapshot.map(
      async (document) => {
        const issue = document.data() as Issue;
        // 広告主が登録した与件の場合
        if (issue.agentId === '' || issue.agentId === null) {
          const client =
            issue.companyId && (await getCompanyById(issue.companyId));
          return client && client.status === status.approved ? issue : null;
        }
        // 代理店が登録した与件の場合
        const agent = issue.agentId && (await getCompanyById(issue.agentId));
        return agent && agent.status === status.approved ? issue : null;
      }
    );

    const gotIssues: Issue[] = (await Promise.all(promises)).filter(
      (issue): issue is Issue => issue !== null
    );

    setIssueList((prevIssuseList) => prevIssuseList.concat(gotIssues));
    setSearchedIssueList((prevIssues) => {
      let result = prevIssues.concat(gotIssues);
      if (searchIssueTitle) {
        result = result.filter((issue: Issue) =>
          issue?.name?.includes(searchIssueTitle)
        );
      }

      if (searchGenre) {
        result = result.filter((issue: Issue) => issue?.genre === searchGenre);
      }

      if (searchBudget) {
        result = result.filter(
          (issue: Issue) => issue?.budget === searchBudget
        );
      }

      if (searchPurpose) {
        result = result.filter(
          (issue: Issue) => issue.purpose && issue?.purpose[0] === searchPurpose
        );
      }

      if (searchMediaType) {
        result = result.filter(
          (issue: Issue) =>
            issue.mediaType && issue?.mediaType[0] === searchMediaType
        );
      }

      if (searchMenu) {
        result = result.filter(
          (issue: Issue) =>
            Array.isArray(issue?.menu) &&
            (issue?.menu as number[]).includes(searchMenu)
        );
      }

      if (searchStartDate.start && searchStartDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchStartDate.start &&
            searchStartDate.end &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') >=
              searchStartDate.start.format('YYYY/MM/DD') &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
        );
      } else if (searchStartDate.start) {
        result = result.filter(
          (value: DocumentData) =>
            searchStartDate.start &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') >=
              searchStartDate.start.format('YYYY/MM/DD')
        );
      } else if (searchStartDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchStartDate.end &&
            dayjs
              .unix((value.startDate as timestamp)?.seconds)
              .format('YYYY/MM/DD') <= searchStartDate.end.format('YYYY/MM/DD')
        );
      }

      if (searchApplicationDate.start && searchApplicationDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchApplicationDate.start &&
            searchApplicationDate.end &&
            dayjs
              .unix((value.applicationDate as timestamp).start?.seconds)
              .format('YYYY/MM/DD') >=
              searchApplicationDate.start.format('YYYY/MM/DD') &&
            dayjs
              .unix((value.applicationDate as timestamp).end?.seconds)
              .format('YYYY/MM/DD') <=
              searchApplicationDate.end.format('YYYY/MM/DD')
        );
      } else if (searchApplicationDate.start) {
        result = result.filter(
          (value: DocumentData) =>
            searchApplicationDate.start &&
            dayjs
              .unix((value.applicationDate as timestamp).start?.seconds)
              .format('YYYY/MM/DD') >=
              searchApplicationDate.start.format('YYYY/MM/DD')
        );
      } else if (searchApplicationDate.end) {
        result = result.filter(
          (value: DocumentData) =>
            searchApplicationDate.end &&
            dayjs
              .unix((value.applicationDate as timestamp).end?.seconds)
              .format('YYYY/MM/DD') <=
              searchApplicationDate.end.format('YYYY/MM/DD')
        );
      }
      // 重複を除外
      result = result.filter(
        (issue, index, self) =>
          index === self.findIndex((i) => i.issueId === issue.issueId)
      );
      return result;
    });
  };

  const loadMore = async (): Promise<void> => {
    if (currentUser === null) return;
    const { docs, empty } = await getIssueInfiniteScrollfromPublisher(
      issueList
    );
    if (empty) {
      setHasMore(false);
      return;
    }
    await appendIssues(docs);
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        await loadMore();
      } catch (e) {
        throwError(e);
      } finally {
        setShowList(true);
        setLoading(false);
      }
    })();
  }, []);

  // メディアリストの件数取得
  const [cantInquiry, setCantInquiry] = useState<string | undefined>(undefined);

  useEffect(() => {
    void (async () => {
      if (currentUser) {
        const userData = await getUserInfo(currentUser.uid);
        const comId = userData?.companyId as string;
        const comRef = doc(companiesRef, comId);
        const comSnap = await getDoc(comRef);
        const comData = comSnap.data();
        if (comData?.status !== status.approved) {
          setCantInquiry(t('issueFavoLike.unApprovedCompany'));
        }
        const q = query(mediasRef, where('companyId', '==', comId));
        const { empty, docs } = await getDocs(q);
        if (empty) {
          setCantInquiry(t('issueFavoLike.noMedia'));
        } else {
          const approvedDocsCount = docs.filter(
            (document) => document.data().status === status.approved
          ).length;
          if (approvedDocsCount === 0) {
            setCantInquiry(t('issueFavoLike.unApprovedMedia'));
          }
        }
      }
    })();
  }, []);

  return (
    <>
      <CustomLoading loading={loading} />
      <Typography>
        <IssueSearchCard handleChange={handleSearchInputChange} />
      </Typography>
      <Typography component="div" sx={{ mb: '40px' }}>
        {showList && (
          <InfiniteScroll
            loadMore={() => {
              void (async () => {
                await loadMore();
              })();
            }}
            hasMore={hasMore}
            loader={<div className="loader" key={0}></div>}
          >
            {searchedIssueList.length !== 0 ? (
              <Typography sx={styles.list} component="div">
                {searchedIssueList.map((issue) => (
                  <Typography
                    key={issue.issueId}
                    sx={styles.card}
                    component="div"
                  >
                    <PublisherIssueCard
                      issue={issue}
                      cantInquiry={cantInquiry}
                    />
                  </Typography>
                ))}
              </Typography>
            ) : (
              <NothingGivenList
                title={t('list.title')}
                detail={t('list.searchNoIssue')}
              />
            )}
          </InfiniteScroll>
        )}
      </Typography>
    </>
  );
};

export default PublisherIssueList;
